// src/ts/main.ts

import { images, ApiError } from "./api/client.js";
import { initSessionKeepalive } from "./session-keepalive.js";

// ---------------------------------------------------------------------------
// Cache constants
// ---------------------------------------------------------------------------

// How long a cached GET /api/ response stays usable.
//
// Short on purpose. The data behind it — comment counts, item statuses, approvals — is
// edited by several people at once, and a write only clears the cache in the browser that
// made it. Anyone else keeps reading stale data until the entry expires, so this is the
// upper bound on how long the application can lie to somebody. A write in your own browser
// still clears the lot immediately; see invalidateApiCache in utils.ts.
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------------------------------------
// Fetch cache
//
// Overrides window.fetch to transparently cache GET /api/* JSON responses
// in localStorage. Cached entries expire after CACHE_TTL_MS.
//
// Bypass the cache for a specific request by passing { cache: "no-store" }
// or { cache: "reload" } in the fetch init options.
//
// Page scripts can invalidate entries via window.wf.invalidateCache(prefix).
// ---------------------------------------------------------------------------

const _nativeFetch = window.fetch.bind(window);

function fetchCacheKey(url: string): string {
  return `wf-fetch:${url}`;
}

// ---------------------------------------------------------------------------
// Whose cache is this?
//
// The cache lives in localStorage, which outlives a sign-in. On a shared machine that meant
// signing out and back in as somebody else could still be shown the previous user's project
// list, for as long as the entries took to expire — their data, on someone else's screen.
//
// So the cache is stamped with who it belongs to, and thrown away the moment that changes.
// The layouts report the signed-in user in a meta tag; signed out it is empty, which counts
// as a change too, so the cache is already gone before the next person signs in.
//
// Stamping rather than clearing on the sign-out button, because there are more ways out than
// that one: a session expiring, a cookie cleared, someone else signing in from a bookmark.
// This catches all of them, since every one of them changes who the page says is signed in.
// ---------------------------------------------------------------------------

const CACHE_OWNER_KEY = "wf-cache-user";

/** Every localStorage key starting with `prefix`. Collected first, because removing while
 *  iterating shifts the indexes underneath. */
function storedKeys(prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(prefix)) keys.push(k);
  }
  return keys;
}

function discardCacheIfUserChanged(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="wf-user"]');

  // No meta tag means a page that does not know either way. Leave the cache alone rather than
  // clearing it on every load of such a page.
  if (!meta) return;

  const current = meta.content;
  if (localStorage.getItem(CACHE_OWNER_KEY) === current) return;

  for (const k of storedKeys("wf-fetch:")) {
    localStorage.removeItem(k);
  }

  localStorage.setItem(CACHE_OWNER_KEY, current);
}

// Runs as the bundle loads. main.js is a plain script at the end of the body while the page
// scripts are deferred, so this happens before anything has had the chance to read the cache.
discardCacheIfUserChanged();

function readFetchCache(url: string): string | null {
  const raw = localStorage.getItem(fetchCacheKey(url));
  if (!raw) return null;
  try {
    const { ts, body } = JSON.parse(raw) as { ts: number; body: string };
    if (Date.now() - ts < CACHE_TTL_MS) return body;
  } catch {}
  localStorage.removeItem(fetchCacheKey(url));
  return null;
}

function writeFetchCache(url: string, body: string, status: number): void {
  try {
    localStorage.setItem(fetchCacheKey(url), JSON.stringify({ ts: Date.now(), body, status }));
  } catch {} // storage quota exceeded — fail silently
}

window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url =
    typeof input === "string" ? input
    : input instanceof URL ? input.href
    : (input as Request).url;

  const method = (
    init?.method ?? (input instanceof Request ? input.method : "GET")
  ).toUpperCase();

  const bypassCache =
    init?.cache === "no-store" || init?.cache === "reload";

  if (method === "GET" && url.startsWith("/api/") && !bypassCache) {
    const cached = readFetchCache(url);
    if (cached !== null) {
      return Promise.resolve(
        new Response(cached, { status: 200, headers: { "content-type": "application/json" } })
      );
    }
    return _nativeFetch(input, init).then(async (res) => {
      const contentType = res.headers.get("content-type") ?? "";

      // Only JSON is cacheable. The cache lives in localStorage, which holds text, so
      // reading a binary body with res.text() decodes it as UTF-8 and rebuilding the
      // Response from that string corrupts it — a downloaded spreadsheet arrives broken.
      // Passing anything else straight through also keeps its headers, Content-Disposition
      // included, which the rebuilt Response below would drop.
      if (!res.ok || !contentType.includes("json")) return res;

      const body = await res.text();
      writeFetchCache(url, body, res.status);
      return new Response(body, {
        status: res.status,
        headers: { "content-type": contentType },
      });
    });
  }

  return _nativeFetch(input, init);
} as typeof window.fetch;

// ---------------------------------------------------------------------------
// Failed thumbnails
//
// When an <img src="/api/images/..."> fails to load we remember the URL so the same missing
// thumbnail is not asked for over and over while the page is open: one grid can hold the same
// image more than once, and re-rendering a filtered list rebuilds every <img> from scratch.
// A MutationObserver intercepts newly added <img> elements and replaces a known-missing
// source with a placeholder before the browser fires the request.
//
// This is held in memory only, so it lasts exactly as long as the page and a refresh always
// retries. It used to be kept in localStorage for 30 minutes, on the assumption that a
// thumbnail missing now would still be missing later. That is wrong for anything the server
// renders after the fact: a STEP import has no thumbnails for the first few minutes, the
// first page view recorded a failure for every one of its items, and refreshing then drew
// the stored placeholder without ever asking again — so renders that had finished long since
// stayed invisible for up to half an hour. Refreshing is exactly what somebody waiting for a
// render does, which made the one thing that should have fixed it the thing that preserved it.
// ---------------------------------------------------------------------------

// Keyed by path and query, so the same image reached through an absolute and a relative URL
// counts as one entry.
const failedThumbnails = new Set<string>();

/**
 * Whether an <img> shows a picture from the API, and so gets the lazy loading, the spinner and
 * the placeholder below. Mostly /api/images/; also a legacy location's pictures, which are
 * files of their own under /api/locations/ but are shown in the same grids.
 */
function isApiPicture(src: string): boolean {
  return src.includes("/api/images/") || src.includes("/api/locations/");
}

function thumbnailKey(src: string): string {
  try {
    const u = new URL(src, location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return src;
  }
}

function hasThumbnailFailed(src: string): boolean {
  return failedThumbnails.has(thumbnailKey(src));
}

function markThumbnailFailed(src: string): void {
  failedThumbnails.add(thumbnailKey(src));
}

/** Forgets every failed thumbnail whose URL contains urlPrefix, so it is requested again. */
function forgetFailedThumbnails(urlPrefix: string): void {
  for (const key of failedThumbnails) {
    if (key.includes(urlPrefix)) failedThumbnails.delete(key);
  }
}

// Clears out what the old localStorage version left behind. Without this, a browser that
// loaded a page before this change keeps its stored failures — and so keeps hiding finished
// renders — until they age out. A no-op once they are gone.
for (const staleKey of storedKeys("wf-img404:")) {
  localStorage.removeItem(staleKey);
}

/**
 * Stands in for a thumbnail that is not there — an item with no file uploaded yet.
 *
 * Styled from the stylesheet rather than inline, because the list views need it a different
 * size and an inline style cannot be overridden by them. What it shows comes from there too:
 * a mark for an item with nothing to show, or a spinner when the tile says a picture is on
 * its way. See image-grid__placeholder and .is-awaiting-render in main.less.
 */
function makePlaceholder(): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = "image-grid__placeholder";
  return span;
}

// Lazy-load observer — fires when a deferred img comes within 200px of the viewport.
const imgLazyObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const img = entry.target as HTMLImageElement;
      imgLazyObserver.unobserve(img);
      const src = img.dataset.wfSrc;
      if (!src) continue;
      delete img.dataset.wfSrc;
      startImgLoad(img, src);
    }
  },
  { rootMargin: "200px 0px" },
);

// Attaches the spinner and initiates the actual HTTP request.
function startImgLoad(img: HTMLImageElement, src: string): void {
  img.style.opacity = "0";
  const spinner = document.createElement("span");
  spinner.className = "image-grid__spinner";
  img.before(spinner);

  img.addEventListener("load", () => {
    spinner.remove();
    img.style.opacity = ""; // triggers CSS transition opacity 0 → 1
  }, { once: true });

  // The spinner is opaque and sits over the whole tile, so it has to come off however the
  // request ends. A thumbnail that is not there yet ends in an error, and the handler that
  // draws the placeholder for it works on the <img>, not on the spinner beside it.
  img.addEventListener("error", () => spinner.remove(), { once: true });

  // A tile's <img> is built with its src already on it, and only the deferred ones — whose src
  // this file stripped off — need it putting back. Assigning the same URL again would throw
  // away a request that is already under way and start it afresh.
  if (img.getAttribute("src") !== src) img.src = src;
}

/**
 * Images already set up, so a second sighting of the same one does nothing.
 *
 * The observer below reports a node every time it is put into the page, and moving an element
 * counts as putting it there again: a grid is appended and then immediately re-ordered by the
 * sort, so every thumbnail arrives here at least twice, within one batch and long before its
 * request has settled. Without this each sighting hung another spinner on the same <img>, and
 * only one of them was ever taken down again — the extras stayed, opaque, over the tile.
 */
const initialisedImgs = new WeakSet<HTMLImageElement>();

// Called from the MutationObserver for each newly added <img>.
function initApiImg(img: HTMLImageElement): void {
  const src = img.getAttribute("src") || img.src || "";
  if (!isApiPicture(src)) return;

  if (initialisedImgs.has(img)) return;
  initialisedImgs.add(img);

  // Already failed on this page — replace immediately, no network request
  if (hasThumbnailFailed(src)) {
    img.replaceWith(makePlaceholder());
    return;
  }

  // Already loaded (browser cache hit)
  if (img.complete && img.naturalWidth > 0) return;

  // If the img is currently near the viewport, load it right away.
  // Otherwise strip the src and defer until it scrolls into range.
  const rect = img.getBoundingClientRect();
  const nearViewport =
    rect.bottom >= -200 &&
    rect.top    <= window.innerHeight + 200 &&
    rect.right  >= 0 &&
    rect.left   <= window.innerWidth;

  if (nearViewport) {
    startImgLoad(img, src);
  } else {
    img.dataset.wfSrc = src;
    img.removeAttribute("src"); // prevents the eager request
    imgLazyObserver.observe(img);
  }
}

new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node instanceof HTMLImageElement) {
        initApiImg(node);
      } else if (node instanceof Element) {
        node.querySelectorAll<HTMLImageElement>("img").forEach(initApiImg);
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

// ---------------------------------------------------------------------------
// Broken-image placeholder
//
// Fires before page scripts create <img>s (module-level, capture:true).
// Also notes the failed URL so the rest of this page load skips it.
// ---------------------------------------------------------------------------
document.addEventListener(
  "error",
  (e) => {
    const target = e.target;
    if (!(target instanceof HTMLImageElement)) return;
    if (!isApiPicture(target.src)) return;

    markThumbnailFailed(target.src);
    // The spinner, if there is one, belongs to startImgLoad and comes off in its own error
    // listener — which still runs after this one, on its way down to the <img> itself.
    target.replaceWith(makePlaceholder());
  },
  { capture: true },
);

// ---------------------------------------------------------------------------
// Global namespace
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    navContext?: { projectId?: number; sceneId?: number };
    populateScenesDropdown?: (projectId: number, sceneId?: number) => Promise<void>;
    showUploadModal?: (
      file: File, imageId: number, steps: { step: number; title: string }[], selectedStep: number,
      onSuccess: () => void) => Promise<void>;
    wf: {
      /** Remove all localStorage fetch-cache entries whose key contains urlOrPrefix. */
      invalidateCache(urlOrPrefix: string): void;
      /** Forget the failed thumbnails whose URL contains urlOrPrefix, so they load again. */
      forgetFailedThumbnails(urlOrPrefix: string): void;
    };
  }
}

window.wf = {
  invalidateCache(urlOrPrefix: string): void {
    const target = fetchCacheKey(urlOrPrefix);
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith("wf-fetch:") && k.includes(urlOrPrefix)) keys.push(k);
    }
    // Also accept exact key match
    if (!keys.length && localStorage.getItem(target) !== null) keys.push(target);
    keys.forEach((k) => localStorage.removeItem(k));
  },
  // Exposed as it is — page scripts need it after an upload, and there is nothing to wrap.
  forgetFailedThumbnails,
};

// ---------------------------------------------------------------------------
// Navbar navigation
// ---------------------------------------------------------------------------

window.populateScenesDropdown = populateScenesDropdown;
window.showUploadModal = showUploadModal;

document.addEventListener("DOMContentLoaded", async () => {
  initThemeSwitcher();
  initBackToTop();
  initSessionKeepalive();
  await Promise.all([
    initProjectsDropdown(),
    initDashboardLink(),
  ]);
  if (window.navContext?.projectId) {
    await populateScenesDropdown(window.navContext.projectId, window.navContext.sceneId);
  }
  initUploadModal();
});

async function initDashboardLink(): Promise<void> {
  const link = document.getElementById("sb-dashboard-link") as HTMLAnchorElement | null;
  if (!link) return;
  try {
    const res = await fetch("/api/clients/my", { credentials: "same-origin" });
    if (!res.ok) return;
    const data = await res.json() as { clientId?: number };
    if (data.clientId) link.href = `/Client/${data.clientId}`;
  } catch {}
}

async function initProjectsDropdown(): Promise<void> {
  const menu = document.getElementById("nav-projects-menu");
  if (!menu) return;

  const currentProjectId = window.navContext?.projectId;

  try {
    const res = await fetch("/api/projects", { credentials: "same-origin" });
    if (!res.ok) return;
    const projects: { id: number; name: string; isStarred: boolean }[] = await res.json();

    if (!projects.length) {
      menu.innerHTML = '<span class="navbar-item has-text-grey is-size-7">No projects</span>';
      return;
    }

    const starred    = projects.filter(p => p.isStarred);
    const notStarred = projects.filter(p => !p.isStarred);

    function itemHtml(p: { id: number; name: string; isStarred: boolean }): string {
      return `<a class="navbar-item${p.id === currentProjectId ? " is-active" : ""}" href="/Project/${p.id}">${escHtml(p.name)}</a>`;
    }

    const parts: string[] = [];
    if (starred.length) {
      parts.push(...starred.map(itemHtml));
      if (notStarred.length) parts.push('<hr class="navbar-divider">');
    }
    parts.push(...notStarred.map(itemHtml));

    menu.innerHTML = parts.join("");
  } catch {}
}

async function populateScenesDropdown(projectId: number, selectedSceneId?: number): Promise<void> {
  const dd = document.getElementById("nav-scenes-dd");
  const menu = document.getElementById("nav-scenes-menu");
  if (!dd || !menu) return;

  try {
    const res = await fetch(`/api/projects/${projectId}/scenes`, { credentials: "same-origin" });
    if (!res.ok) return;
    const scenes: any[] = await res.json();
    if (!scenes.length) return;

    menu.innerHTML = scenes
      .map(
        (s: any) =>
          `<a class="navbar-item${s.id === selectedSceneId ? " is-active" : ""}" href="/Scene/${s.id}">${escHtml(s.name)}</a>`,
      )
      .join("");

    const trigger = document.getElementById("nav-scenes-trigger");
    if (trigger) {
      const current = scenes.find((s: any) => s.id === selectedSceneId);
      trigger.textContent = current ? current.name : "Scenes";
    }

    dd.style.display = "";
  } catch {}
}

function escHtml(s: string): string {
  const d = document.createElement("div");
  d.textContent = s ?? "";
  return d.innerHTML;
}

// ---------------------------------------------------------------------------
// Back to top
// ---------------------------------------------------------------------------

/** How far down the page has to have gone before the button is worth offering. */
const BACK_TO_TOP_AFTER = 100;

/**
 * A button in the bottom corner that returns to the top of the page.
 *
 * Built here rather than put in the layouts, because there are two of them and this belongs on
 * every page under both.
 */
function initBackToTop(): void {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "back-to-top";
  button.title = "Back to top";
  button.setAttribute("aria-label", "Back to top");
  button.innerHTML = '<span class="material-symbols-outlined">arrow_upward</span>';

  button.addEventListener("click", () => {
    // A jump rather than a glide for anyone who has asked their system for less movement.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  });

  document.body.appendChild(button);

  // scroll fires far more often than the page can paint, so the work is held to one frame at a
  // time and the class is only touched when the answer has actually changed.
  let queued = false;
  let shown = false;

  const update = (): void => {
    queued = false;
    const should = window.scrollY > BACK_TO_TOP_AFTER;
    if (should === shown) return;
    shown = should;
    button.classList.toggle("is-visible", shown);
  };

  window.addEventListener("scroll", () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }, { passive: true });

  // A reload can restore the previous scroll position, so do not assume the page starts at 0.
  update();
}

// ---------------------------------------------------------------------------
// Theme switcher
// ---------------------------------------------------------------------------

function initThemeSwitcher(): void {
  updateThemeActive();
  document.querySelectorAll<HTMLElement>("[data-theme-id]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      applyTheme(el.getAttribute("data-theme-id")!);
    });
  });
}

function applyTheme(id: string): void {
  document.documentElement.setAttribute("data-theme", id);
  localStorage.setItem("wf-theme", id);
  updateThemeActive();
}

function updateThemeActive(): void {
  const current = document.documentElement.getAttribute("data-theme") ?? "light";
  document.querySelectorAll<HTMLElement>("[data-theme-id]").forEach((el) => {
    el.classList.toggle("is-active", el.getAttribute("data-theme-id") === current);
  });
}

// ---------------------------------------------------------------------------
// Upload modal
// ---------------------------------------------------------------------------

let _uploadFile: File | null = null;
let _uploadImageId: number | null = null;
let _uploadCallback: (() => void) | null = null;

function initUploadModal(): void {
  const modal = document.getElementById("upload-modal");
  if (!modal) return;

  document.getElementById("upload-modal-close")?.addEventListener("click", closeUploadModal);
  document.getElementById("upload-cancel-btn")?.addEventListener("click", closeUploadModal);
  document.getElementById("upload-modal-bg")?.addEventListener("click", closeUploadModal);

  document.getElementById("upload-submit-btn")?.addEventListener("click", submitUpload);
}

/** One production-flow step the new version can be filed at. */
interface UploadStep { step: number; title: string }

/**
 * `steps` are the steps of the image's scene's production flow, which only the caller knows;
 * `selectedStep` starts chosen — the step the newest version is at, normally.
 */
async function showUploadModal(
  file: File, imageId: number, steps: UploadStep[], selectedStep: number, onSuccess: () => void,
): Promise<void> {
  _uploadFile = file;
  _uploadImageId = imageId;
  _uploadCallback = onSuccess;

  const errorEl = document.getElementById("upload-error");
  if (errorEl) { errorEl.textContent = ""; errorEl.classList.add("is-hidden"); }

  const internalCb = document.getElementById("upload-internal") as HTMLInputElement | null;
  if (internalCb) internalCb.checked = false;

  const fileInfo = document.getElementById("upload-file-info");
  if (fileInfo) {
    const kb = (file.size / 1024).toFixed(1);
    fileInfo.innerHTML = `<strong>${escHtml(file.name)}</strong> <span class="tag is-neutral">${kb} KB</span>`;
  }

  // Filled every time: images in different scenes follow different flows.
  const sel = document.getElementById("upload-status-select") as HTMLSelectElement | null;
  if (sel) {
    sel.innerHTML = steps
      .map((s) => `<option value="${s.step}"${s.step === selectedStep ? " selected" : ""}>${s.step}. ${escHtml(s.title)}</option>`)
      .join("");
  }

  document.getElementById("upload-modal")?.classList.add("is-active");
}

function closeUploadModal(): void {
  document.getElementById("upload-modal")?.classList.remove("is-active");
  _uploadFile = null;
  _uploadImageId = null;
  _uploadCallback = null;
}

async function submitUpload(): Promise<void> {
  if (!_uploadFile || !_uploadImageId) return;

  const sel = document.getElementById("upload-status-select") as HTMLSelectElement | null;
  const internalCb = document.getElementById("upload-internal") as HTMLInputElement | null;
  const btn = document.getElementById("upload-submit-btn") as HTMLButtonElement | null;
  const errorEl = document.getElementById("upload-error");

  // The production-flow step; the endpoint calls it imageStatusId, a name older than flows.
  const step = sel ? parseInt(sel.value) : 0;
  const isInternal = internalCb?.checked ?? false;

  if (btn) { btn.classList.add("is-loading"); btn.disabled = true; }
  if (errorEl) { errorEl.textContent = ""; errorEl.classList.add("is-hidden"); }

  const form = new FormData();
  form.append("file", _uploadFile);
  form.append("imageStatusId", String(step));
  form.append("isInternal", String(isInternal));

  const imageId = _uploadImageId;

  try {
    const res = await fetch(`/api/images/${imageId}/upload`, {
      method: "POST",
      credentials: "same-origin",
      body: form,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error((body as any)?.message ?? `Upload failed (${res.status})`);
    }

    // A video needs a still beside it before it can appear in any list, and the server makes
    // one as the file lands. It is allowed to fail — ffmpeg may not be installed — and when
    // it does the upload is still good, so this is said rather than thrown: without it the
    // video would simply turn up in the lists as a blank tile with no explanation.
    const result = await res.json().catch(() => null) as
      { isVideo?: boolean; posterMade?: boolean } | null;

    // Said in a dialog rather than in the modal's own error line: the callers close this
    // modal and reload the page, so anything written into it here is never read.
    if (result?.isVideo && result.posterMade === false) {
      alert("The video was uploaded, but no thumbnail could be made for it. "
        + "It will appear in the lists without a picture.");
    }

    // A new file version is now available, so a thumbnail that failed earlier in this page
    // load should be requested again rather than left as a placeholder.
    forgetFailedThumbnails(`/api/images/${imageId}`);

    // Everything cached under /api/, not just this image: a new version changes the scene
    // and project listings too — item counts, statuses, newest-file timestamps. Clearing
    // only this image's entries left those pages showing the state from before the upload.
    window.wf.invalidateCache("/api/");

    const cb = _uploadCallback;
    closeUploadModal();
    cb?.();
  } catch (err: any) {
    if (errorEl) { errorEl.textContent = err.message; errorEl.classList.remove("is-hidden"); }
  } finally {
    if (btn) { btn.classList.remove("is-loading"); btn.disabled = false; }
  }
}
